import React from "react";
import { useTranslation } from "react-i18next";
import {
  ShieldCheckIcon,
  ComputerDesktopIcon,
  WifiIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { ratingTone } from "../utils/participantStats";

// good: true -> green check. false -> red x, or an amber warning when
// severity="warn" (degraded network / busy CPU are advisory, not failures).
// null/undefined -> no badge.
const Badge = ({ good, severity = "error" }) => {
  if (good == null) return null;
  if (good) {
    return <CheckCircleIcon className="h-3.5 w-3.5 shrink-0 text-green-600" />;
  }
  return severity === "warn" ? (
    <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" />
  ) : (
    <XCircleIcon className="h-3.5 w-3.5 shrink-0 text-red-500" />
  );
};

// The value truncates rather than wraps: every card has a fixed row count, so a
// wrapped row grows the card and reintroduces page scroll. `title` keeps the
// full string reachable on hover.
const Row = ({ label, value, good, severity }) => (
  <div className="flex min-w-0 items-center justify-between gap-2 py-0.5">
    <span className="shrink-0 text-xs text-gray-500">{label}</span>
    <span className="flex min-w-0 items-center gap-1 text-xs font-medium">
      <span
        title={typeof value === "string" ? value : undefined}
        className={`truncate ${good === false ? "text-red-600" : "text-gray-900"}`}
      >
        {value ?? "—"}
      </span>
      <Badge good={good} severity={severity} />
    </span>
  </div>
);

// `h-full` so a card with fewer rows still fills its grid cell — the grid
// stretches every card in a row to the tallest one, and this makes the border
// follow. Needed for Device too, which sits inside a col-span wrapper.
const Card = ({ icon: Icon, title, headerRight, children }) => (
  <div className="h-full rounded-xl border border-gray-200 bg-white p-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50">
          <Icon className="h-4 w-4 text-orange-500" />
        </span>
        <h3 className="mb-0 text-sm font-bold text-gray-900">{title}</h3>
      </div>
      {headerRight}
    </div>
    <hr className="my-2 border-t border-gray-200" />
    {children}
  </div>
);

// Three bands, matching the score: good -> green, warn (Fair) -> amber, bad
// (Poor) -> red.
const TONE_TEXT = {
  good: "text-gray-900",
  warn: "text-amber-600",
  bad: "text-red-600",
};

const RatingHeader = ({ rating }) => {
  const { t } = useTranslation();
  if (!rating) return null;
  const tone = ratingTone(rating);
  return (
    <span className="flex shrink-0 items-center gap-1 text-xs font-medium">
      <span className={TONE_TEXT[tone]}>{t(rating)}</span>
      <Badge good={tone === "good"} severity={tone === "warn" ? "warn" : "error"} />
    </span>
  );
};

const asPercent = (n) => (typeof n === "number" ? `${n}%` : "—");
const asKbps = (n) => (typeof n === "number" ? `${n} kbps` : "—");
const asFps = (n) => (typeof n === "number" ? `${n} FPS` : "—");

const ParticipantStatsPanel = ({ stats }) => {
  const { t } = useTranslation();
  const v = stats?.verification || {};
  const d = stats?.device || {};
  const p = stats?.performance || {};
  const c = stats?.connectivity || {};
  const a = stats?.audio || {};
  const vid = stats?.video || {};

  const cpuRow = (
    <Row
      label={t("CPU Status")}
      value={p.cpuStatus ? t(p.cpuStatus) : "—"}
      good={p.cpuStatus == null ? null : p.cpuStatus === "Normal"}
      severity="warn"
    />
  );

  // Both scores badge off the verdict their own endpoint returned, never off a
  // threshold on the number. /verify can answer { verified: false, score: 0.55 }
  // — over half, but rejected, and the pipeline acted on the rejection. A
  // `score >= 50` badge would put a green check next to that.
  const faceMatchGood = v.faceMatch == null ? null : v.faceMatch === "Matched";

  const verificationCard = (
    <Card icon={ShieldCheckIcon} title={t("Verification")}>
      <Row
        label={t("Face Match Status")}
        value={v.faceMatch ? t(v.faceMatch) : "—"}
        good={faceMatchGood}
      />
      <Row
        label={t("Face Match Score")}
        value={asPercent(v.faceMatchScore)}
        good={v.faceMatchScore == null ? null : faceMatchGood}
      />
      <Row
        label={t("Liveness Score")}
        value={asPercent(v.liveness)}
        good={v.liveness == null ? null : v.livenessIsLive ?? null}
      />
      <Row label={t("Video Resolution")} value={v.resolution || "—"} />
    </Card>
  );

  const deviceCard = (
    <Card icon={ComputerDesktopIcon} title={t("Device")}>
      <Row label={t("Microphone")} value={d.microphone || "—"} />
      <Row label={t("Speaker")} value={d.speaker || "—"} />
      <Row label={t("Camera")} value={d.camera || "—"} />
    </Card>
  );

  // CPU Status lives here rather than in a Performance card of its own: both rows
  // come from the same onQualityLimitation payload, and a card holding a single
  // row wastes a lot of vertical space.
  const connectivityCard = (
    <Card icon={WifiIcon} title={t("Connectivity")}>
      <Row
        label={t("Network Status")}
        value={c.networkStatus ? t(c.networkStatus) : "—"}
        good={c.networkStatus == null ? null : c.networkStatus === "Stable"}
        severity="warn"
      />
      {cpuRow}
    </Card>
  );

  const audioCard = (
    <Card icon={SpeakerWaveIcon} title={t("Audio")} headerRight={<RatingHeader rating={a.rating} />}>
      <Row label={t("Packet Loss")} value={asPercent(a.packetLoss)} />
      <Row label={t("Bitrate")} value={asKbps(a.bitrate)} />
    </Card>
  );

  const videoCard = (
    <Card icon={VideoCameraIcon} title={t("Video")} headerRight={<RatingHeader rating={vid.rating} />}>
      <Row label={t("Packet Loss")} value={asPercent(vid.packetLoss)} />
      <Row label={t("Bitrate")} value={asKbps(vid.bitrate)} />
      <Row label={t("Frame Rate")} value={asFps(vid.frameRate)} />
    </Card>
  );

  // One layout everywhere. It has to survive the narrowest host — the 40%-wide
  // column beside the video on Video Consent — so it lays out two-across rather
  // than three; three cards in that width would crush the labels. Device spans
  // both columns because its values are OS device names, the longest strings in
  // the panel, and spanning it also keeps the odd fifth card from leaving a hole.
  //
  // No `items-*`: the grid's default `stretch` makes both cards in a row match
  // the taller one, so a card with fewer rows (Audio) does not leave dead space
  // beneath it.
  return (
    <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
      {verificationCard}
      {audioCard}
      {videoCard}
      {connectivityCard}
      <div className="md:col-span-2">{deviceCard}</div>
    </div>
  );
};

export default ParticipantStatsPanel;
